import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRight, CheckSquare, FolderKanban, Play, Timer, UsersRound } from 'lucide-react';
import { dashboardQueryOptions } from '~/api/analysis';
import { useMe } from '~/api/auth';
import { PROJECT_STATUS_LABEL, projectsQueryOptions } from '~/api/projects';
import { ProjectDot } from '~/features/projects/ProjectColor';
import { useServerClock } from '~/api/serverClock';
import { meActiveQueryOptions, meDayQueryOptions } from '~/api/work';
import { ClockCard } from '~/features/clock/ClockCard';
import { ActivityBars } from '~/features/dashboard/ActivityBars';
import { OccurrenceRow } from '~/features/work/OccurrenceRow';
import { TimeTracker } from '~/features/work/TimeTracker';
import { describeDue, formatLongDate, formatMinutes } from '~/lib/format';
import { Card } from '~/ui/Card';
import { Donut } from '~/ui/Donut';
import { EmptyState } from '~/ui/EmptyState';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { PageHeader } from '~/ui/PageHeader';
import { Pill } from '~/ui/Pill';
import { Skeleton } from '~/ui/Skeleton';
import { StatCard } from '~/ui/StatCard';
import type { Tone } from '~/ui/tones';

export const Route = createFileRoute('/_app/dashboard')({
  component: DashboardPage,
});

const PROJECT_TONE: Record<string, Tone> = {
  planning: 'neutral',
  active: 'accent',
  on_hold: 'warn',
  done: 'done',
  archived: 'neutral',
};

function DashboardPage() {
  const me = useMe();
  const now = useServerClock(30_000);
  const tz = me.person.tz ?? me.workspace.tz;
  const dashboard = useQuery(dashboardQueryOptions);
  const day = useQuery(meDayQueryOptions());
  const active = useQuery(meActiveQueryOptions);
  const projects = useQuery(projectsQueryOptions());
  const isMember = me.person.role === 'member';

  const d = dashboard.data;
  const next =
    day.data?.occurrences.find(
      (o) => o.status === 'pending' || o.status === 'paused' || o.status === 'missed',
    ) ?? null;

  return (
    <div className="mx-auto max-w-[1240px]">
      <PageHeader
        eyebrow="Dashboard"
        title={formatLongDate(now, tz)}
        subtitle={
          isMember
            ? 'Your duties, your timer, and where the projects stand.'
            : 'Your team, your duties, and where the projects stand.'
        }
        actions={
          <Link
            to="/tasks"
            search={{ view: 'board', filter: 'mine' }}
            data-tour="dash-board"
            className="inline-flex h-10 items-center gap-2 rounded-control bg-accent px-4 text-sm font-medium text-on-accent hover:bg-accent-ink"
          >
            <CheckSquare size={16} aria-hidden />
            Open the board
          </Link>
        }
      />

      <div className="mb-4" data-tour="dash-clock">
        <ClockCard />
      </div>

      {dashboard.isError ? (
        <ErrorNotice error={dashboard.error} onRetry={() => dashboard.refetch()} />
      ) : (
        <>
          <div
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
            data-tour="dash-stats"
          >
            <StatCard
              lead
              label="Projects"
              value={d?.projects.total ?? 0}
              delta={d ? d.projects.newThisMonth - d.projects.newLastMonth : null}
              deltaLabel="from last month"
              icon={FolderKanban}
            />
            <StatCard
              label="Running"
              value={d?.projects.running ?? 0}
              note="Projects with work in progress"
              icon={Play}
              delay={40}
            />
            <StatCard
              label="Pending"
              value={d?.projects.pending ?? 0}
              note="Planning or on hold"
              icon={Timer}
              delay={80}
            />
            <StatCard
              label="Finished this week"
              value={d?.week.done ?? 0}
              delta={d ? d.week.done - d.week.donePrevious : null}
              deltaLabel="from last week"
              icon={CheckSquare}
              delay={120}
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card
              title="Activity"
              eyebrow={isMember ? 'Your week' : 'Department, this week'}
              delay={160}
            >
              {d ? (
                <>
                  <ActivityBars activity={d.activity} today={d.date} />
                  <p className="mt-3 text-xs text-ink-3">
                    {formatMinutes(d.week.minutes)} in duties this week
                    {d.week.minutesPrevious > 0 &&
                      ` · ${formatMinutes(d.week.minutesPrevious)} the week before`}
                  </p>
                </>
              ) : (
                <Skeleton lines={5} />
              )}
            </Card>

            <Card
              title="Projects"
              eyebrow="Where things stand"
              delay={200}
              actions={
                <Link
                  to="/projects"
                  className="inline-flex items-center gap-1 text-sm text-accent hover:underline"
                >
                  All <ArrowRight size={14} aria-hidden />
                </Link>
              }
            >
              {projects.isPending ? (
                <Skeleton lines={5} />
              ) : projects.isError ? (
                <ErrorNotice error={projects.error} onRetry={() => projects.refetch()} />
              ) : projects.data.filter((p) => p.status !== 'archived').length === 0 ? (
                <p className="text-sm text-ink-3">
                  No projects yet. Group related tasks into one when the time comes.
                </p>
              ) : (
                <ul className="divide-y divide-line">
                  {projects.data
                    .filter((p) => p.status !== 'archived')
                    .slice(0, 6)
                    .map((p) => (
                      <li key={p.id}>
                        <Link
                          to="/projects/$id"
                          params={{ id: p.id }}
                          className="-mx-2 flex items-center gap-3 rounded-control px-2 py-2.5 hover:bg-paper-2"
                        >
                          <ProjectDot color={p.color} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">{p.name}</span>
                            <span className="block text-xs text-ink-3">
                              {p.targetDate && d
                                ? `Due ${describeDue(p.targetDate, d.date)}`
                                : `${p.members.length} people`}
                            </span>
                          </span>
                          <Pill tone={PROJECT_TONE[p.status] ?? 'neutral'}>
                            {PROJECT_STATUS_LABEL[p.status]}
                          </Pill>
                        </Link>
                      </li>
                    ))}
                </ul>
              )}
            </Card>

            <div data-tour="dash-tracker">
              {active.data ? (
                <TimeTracker
                  active={active.data.active}
                  serverTime={active.data.serverTime}
                  next={next}
                  delay={240}
                />
              ) : (
                <Card tone="deep" delay={240}>
                  <Skeleton lines={4} />
                </Card>
              )}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card
              title="Today"
              dataTour="dash-today"
              eyebrow={
                day.data
                  ? `${day.data.occurrences.filter((o) => o.status === 'done').length} of ${day.data.occurrences.length} done`
                  : 'Your duties'
              }
              className="lg:col-span-2"
              delay={280}
              actions={
                day.data && day.data.missedYesterday.length > 0 ? (
                  <Pill tone="late">{day.data.missedYesterday.length} missed yesterday</Pill>
                ) : undefined
              }
            >
              {day.isPending ? (
                <Skeleton lines={5} />
              ) : day.isError ? (
                <ErrorNotice error={day.error} onRetry={() => day.refetch()} />
              ) : day.data.occurrences.length === 0 && day.data.missedYesterday.length === 0 ? (
                <EmptyState
                  icon={<CheckSquare aria-hidden />}
                  title="Nothing on your day yet."
                  body={
                    day.data.poolCount > 0
                      ? `${day.data.poolCount} tasks in the pool are waiting for someone.`
                      : undefined
                  }
                  action={
                    day.data.poolCount > 0 ? (
                      <Link
                        to="/tasks"
                        search={{ view: 'board', filter: 'all' }}
                        className="text-sm text-accent hover:underline"
                      >
                        Pick one from the board
                      </Link>
                    ) : undefined
                  }
                />
              ) : (
                <ul className="divide-y divide-line">
                  {day.data.missedYesterday.map((o) => (
                    <OccurrenceRow key={o.id} occurrence={o} serverTime={day.data.serverTime} />
                  ))}
                  {day.data.occurrences.map((o) => (
                    <OccurrenceRow key={o.id} occurrence={o} serverTime={day.data.serverTime} />
                  ))}
                </ul>
              )}
            </Card>

            <Card
              title={isMember ? 'Progress' : 'Right now'}
              eyebrow={isMember ? 'Running projects' : 'Your team'}
              delay={320}
            >
              {!d ? (
                <Skeleton lines={5} />
              ) : !isMember && d.team ? (
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-center">
                    <Donut
                      percent={
                        d.team.working + d.team.idle === 0
                          ? 0
                          : (d.team.working / (d.team.working + d.team.idle)) * 100
                      }
                      label="on a duty"
                      segments={[
                        { value: d.team.working, color: 'var(--accent)', label: 'Working' },
                        { value: d.team.idle, color: 'var(--warn)', label: 'Idle' },
                        { value: d.team.off, color: 'var(--line)', label: 'Off today' },
                      ]}
                    />
                  </div>
                  <ul className="grid grid-cols-3 gap-2 text-center text-xs">
                    <li>
                      <span className="block font-display text-lg font-semibold tabular">
                        {d.team.working}
                      </span>
                      <span className="text-ink-3">working</span>
                    </li>
                    <li>
                      <span className="block font-display text-lg font-semibold tabular">
                        {d.team.idle}
                      </span>
                      <span className="text-ink-3">idle</span>
                    </li>
                    <li>
                      <span className="block font-display text-lg font-semibold tabular">
                        {d.team.off}
                      </span>
                      <span className="text-ink-3">off today</span>
                    </li>
                  </ul>
                  <Link
                    to="/team"
                    search={{ group: 'all' }}
                    className="inline-flex items-center gap-1 text-sm text-accent hover:underline"
                  >
                    <UsersRound size={14} aria-hidden /> See the team
                  </Link>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <Donut percent={d.projects.averageProgress ?? 0} label="average progress" />
                  <p className="text-center text-xs text-ink-3">
                    {d.projects.running === 0
                      ? 'No running projects.'
                      : `Across ${d.projects.running} running ${d.projects.running === 1 ? 'project' : 'projects'}.`}
                  </p>
                  <p className="text-center text-xs text-ink-3">
                    {d.duties.upcoming} duties coming up in the next two weeks.
                  </p>
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
